import { QueryBuilderConfig, RuleSet } from 'ngx-angular-query-builder';
import { Injectable } from "@angular/core";
import { ITable, IResizedColumn } from "@common/models/table";
import { ApplicationState } from "@feature/store/application-store";
import { select, Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { distinctUntilChanged, filter, map, tap } from "rxjs/operators";
import { IColumnsState } from "../models/table";
import { queryBuilderConfig, selectAllColumns, selectColumns, selectIsLoading, selectTable, selectWidgetTable, selectAllValuesFromOneColumnAsArray, selectSummedFooter } from "../ngrx/table.selectors";
import { IColumnDefinition } from "@common/models/column-definition";
import * as tableActions from "@feature/_app_modules/shared/table/ngrx/table.actions";
import { EntityKey } from '@pma-entity/pma-entity-types';
import { WidgetAndConfigId } from '@common/models/widget-and-config-id';
import * as filterSelectors from "@common/filter/selectors/filter.selectors";
import * as filterActions from '@common/filter/actions/filter.actions';

@Injectable()
export class TableFacadeService {

  constructor(private readonly store: Store<ApplicationState>) {}

  getLoading(entityName: string): Observable<boolean> {
    return this.store.pipe(
      select(selectIsLoading(entityName)),
      distinctUntilChanged()
    );
  }

  loadTableData(entityName: string, key: EntityKey | undefined, widgetId?: number) {
    this.store.dispatch(
      tableActions.loadTable({ id: widgetId ? widgetId.toString() : entityName, entityName, key, widgetId })
    );
  }

  loadAvailableColumns(entityName: string, widgetId?: number){
    this.store.dispatch(
      tableActions.getAvailableColumns({
        id:  widgetId ? widgetId.toString() : entityName,
        entityName,
        widgetId
      })
    );
  }

  getTable(id: string): Observable<ITable> {
    return this.store.pipe(
      select(selectTable(id)),
      filter((state) => !state?.isLoading),
      map((state) => state?.table),
      filter((table) => table !== undefined)
    );
  }

  getAllValuesFromOneColumnAsArray(id: string, widgetConfigId: number, fieldName: string): Observable<any[]> {
    return this.store.pipe(
      select(selectAllValuesFromOneColumnAsArray(id, widgetConfigId, fieldName))
    );
  }

  getSumValues(id: string, widgetConfigId: number): Observable<any>{
    return this.store.pipe(
      select(selectSummedFooter(id, widgetConfigId))
    );
  }

  getUnfilteredWidgetTable(widgetId: string): Observable<ITable> {
    return this.store.pipe(select(selectTable(widgetId)), map(tableState => tableState.table));
  }

  getWidgetTable(widgetId: string, widgetConfigId: number): Observable<ITable> {
    return this.store.pipe(
      select(selectWidgetTable(widgetId, widgetConfigId)),
      filter((state) => !state?.isLoading),
      map((state) => state?.table),
      filter((table) => table !== undefined)
    );
  }

  getQueryBuilderConfig(widgetId: string, widgetConfigId: number): Observable<QueryBuilderConfig> {
    return this.store.pipe(select(queryBuilderConfig(widgetId, widgetConfigId)));
  }

  getAvailableColumns(id: string): Observable<IColumnDefinition[]> {
    return this.store.pipe(
      select(selectColumns(id)),
      filter((state) => !state?.isLoading),
      map((state) => state?.columns),
      filter((columns) => columns !== undefined)
    );
  }

  getAllColumns(entityName: string): Observable<IColumnDefinition[]> {
    return this.store.pipe(
      select(selectAllColumns(entityName)),
      tap((state: IColumnsState) => {
        if (!state?.columns && !state?.isLoading) {
          this.store.dispatch(
            tableActions.getAllColumns({
              id: entityName,
              entityName
            })
          );
        }
      }),
      map((state) => state?.columns),
      filter((columns) => columns != undefined)
    );
  }

  setSelectedColumns(entityName: string, selectedColumns: IColumnDefinition[], key: EntityKey | undefined, widgetId?: number) : void {
    this.store.dispatch(
      tableActions.setSelectedColumns({
        id: widgetId ? widgetId.toString() : entityName,
        entityName,
        selectedColumns,
        key,
        widgetId
      })
    );
  }

  setColumnOrder(entityName: string, orderedColumns: string[], widgetId?: number): void {
    this.store.dispatch(
      tableActions.reorderColumns({
        id: entityName,
        entityName,
        orderedColumns,
        widgetId
      })
    );
  }

  setColumnSize(entityName: string, resizedColumn: IResizedColumn, widgetId?: number): void {
    this.store.dispatch(
      tableActions.resizeColumn({
        id: entityName,
        entityName,
        resizedColumn,
        widgetId
      })
    );
  }

  resetColumns(entityName: string, key: EntityKey | undefined, widgetId?: number): void {
    this.store.dispatch(
      tableActions.resetColumnsToDefault({
        id: widgetId ? widgetId.toString() : entityName,
        entityName,
        key,
        widgetId
      })
    );
  }

  userFilter(widget: WidgetAndConfigId) {
    return this.store.pipe(select(filterSelectors.getWidgetFilter(widget.id))).pipe(
      distinctUntilChanged(),
    );
  }

  saveFilter(widgetId: number, filter: RuleSet) {
    this.store.dispatch(new filterActions.UpdateWidgetFilter(new filterActions.WidgetFilterActionPayload(widgetId, filter)));
  }

  loadFilter(widgetId: number){
    this.store.dispatch(
      new filterActions.LoadWidgetFilter(
        new filterActions.WidgetFilterActionPayload(widgetId),
      ),
    );
  }
}
